import LessonsSchedule from 'components/LessonsSchedule/LessonsSchedule';
import { format } from 'date-fns';
import { Lesson } from 'pages/Education/Education.types';
import { PurchasedSubscription } from 'pages/Personal/Personal.types';
import React, {
  BaseSyntheticEvent, useEffect, useRef, useState,
} from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useTranslation } from 'react-i18next';
import useChildren from 'src/hooks';
import { scheduleDays } from 'src/hooks/useLessonsSchedule';
import usePost from 'src/hooks/usePost';
import usePurchasedSubscriptions from 'src/hooks/usePurchasedSubscriptions';
import Page from 'src/layout/Page';
import './booking.css';
import { useLocation } from 'react-router-dom';
import { Value } from 'src/shared/types';

const Booking: React.FC = (): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [calendarValue, setCalendarValue] = useState<Value>('');
  const [activeDay, setActiveDay] = useState<number | undefined>(undefined);
  const [activeLesson, setActiveLesson] = useState<Lesson | undefined | null>(
    undefined,
  );
  const [successBooking, setSuccessBooking] = useState<boolean | undefined>();
  const [maxDate, setMaxDate] = useState<Date>();
  const [reFetch, setReFetch] = useState<boolean>(true);
  const [activeSubscriptionId, setActiveSubscriptionId] = useState<string | number>(-1);
  const [calendarLanguage, setCalendarLanguage] = useState(i18n.language);
  const { makeRequest, error, invalidateError } = usePost();
  const { state: navigationState } = useLocation();
  const { children } = useChildren();

  useEffect(() => {
    document.title = t('pages.booking.title') as string;
    setCalendarLanguage(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    children?.map((c: any, index: number) => {
      if (navigationState !== undefined && navigationState?.childId === c.id) {
        setActiveIndex(index);
      }
      return c;
    });
  }, [children?.length]);

  const subscriptionSelectRef = useRef() as React.MutableRefObject<HTMLSelectElement>;

  const { purchasedSubscriptions } = usePurchasedSubscriptions(reFetch);

  useEffect(() => {
    if (purchasedSubscriptions) {
      const sId = typeof activeSubscriptionId === 'number' ? activeSubscriptionId : parseInt(activeSubscriptionId, 10);
      const endDate = sId !== -1 ? purchasedSubscriptions.filter((s) => s.id === sId)[0].endDate
        : purchasedSubscriptions[0].endDate;
      const date = endDate
        ? new Date(endDate)
        : new Date();
      setMaxDate(date);
    }
  }, [activeSubscriptionId, activeLesson?.id]);

  const handleActiveChildren = (e: BaseSyntheticEvent, index: number) => {
    e.preventDefault();
    setActiveIndex(index);
  };

  const preventSubmit = async (e: BaseSyntheticEvent) => {
    e.preventDefault();
    invalidateError();
    const selectedDate = format(calendarValue as Date, 'yyyy-MM-dd');
    const response = await makeRequest('lessons-booking', true, {
      childId: children[activeIndex].id,
      lessonScheduleId: activeLesson?.id,
      purchasedSubscriptionId: subscriptionSelectRef.current.value,
      bookingDate: selectedDate,
    });
    if (response && response?.success !== false) {
      setSuccessBooking(true);
      setReFetch(!reFetch);
    }
  };

  const handleLessonSelection = (lesson: Lesson | null) => {
    setActiveLesson(lesson);
    setCalendarValue('');
    setSuccessBooking(false);
    invalidateError();
    if (lesson) {
      setActiveDay(scheduleDays.indexOf(lesson.dayOfWeek));
    }
    if (lesson === null) {
      setActiveDay(undefined);
    }
  };

  const onChange = (val: any) => {
    setCalendarValue(val);
    setSuccessBooking(false);
    invalidateError();
  };

  const disabledTile = (dateProps: any) => {
    const { date } = dateProps;
    if (typeof activeDay !== 'number') {
      return false;
    }
    return date.getDay() !== activeDay;
  };

  const handleSubscriptionChange = () => {
    setActiveSubscriptionId(subscriptionSelectRef?.current?.value);
    setSuccessBooking(false);
    invalidateError();
  };

  const subscriptionName = (subscription: any) => {
    const initialLessons = `${subscription.lessonsCountInitial} ${t('pages.booking.lessons')}`;
    const lessonsLeft = `${t('pages.booking.lessonsLeft', { count: subscription.lessonsCountLeft })}`;
    const expire = `${t('pages.booking.expire')} ${subscription.endDate}`;
    const isOnline = subscription.isOnline
      ? t('pages.buySubscription.digital')
      : t('pages.buySubscription.frontal');
    return (
      <>
        {initialLessons} ({lessonsLeft}) - {expire} [{isOnline}]
      </>
    );
  };

  return (
    <Page pageClass={'booking-page'}>
      <div className="content-wrapper">
        <div className={'children-container'}>
          {children?.map((child: any, index: number) => (
            <div
              className={`child ${activeIndex === index ? 'active' : ''}`}
              key={child.id}
              onClick={(e) => handleActiveChildren(e, index)}
            >
              {child.firstname} {child.lastname}
            </div>
          ))}
        </div>
        <LessonsSchedule
          isClickableLessons={true}
          emitLessonClick={handleLessonSelection}
        />
        <div className="form-container">
          <select
            name="subscriptions"
            id="subscriptions"
            ref={subscriptionSelectRef}
            onChange={handleSubscriptionChange}
            value={parseInt(activeSubscriptionId as string, 10)}
          >
            {purchasedSubscriptions?.map(
              (subscription: PurchasedSubscription) => (
                <option value={subscription.id} key={subscription.id}>
                  {subscriptionName(subscription)}
                </option>
              ),
            )}
          </select>
          <Calendar
            view={'month'}
            onChange={(value: any) => onChange(value)}
            value={calendarValue}
            tileDisabled={disabledTile}
            calendarType={'hebrew'}
            locale={calendarLanguage}
            maxDate={maxDate as Date}
            minDate={new Date()}
          />
        </div>
        {error && <div className={'error'}>{t(`errors.${error.errorCode}`)}</div>}
        {successBooking && (
          <div className="success">{t('common.successfullyBooked')}</div>
        )}
        <div className="submit-container">
          <button
            onClick={preventSubmit}
            className={'book-button'}
            disabled={!activeLesson || calendarValue === ''}
          >
            {t('pages.booking.book')}
          </button>
        </div>
      </div>
    </Page>
  );
};

export default Booking;
